import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const shimmerSweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
`;

export const Page = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 16px 72px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const BackButton = styled.button`
  background: linear-gradient(
    180deg,
    rgba(31, 42, 64, 0.62) 0%,
    rgba(18, 24, 36, 0.54) 100%
  );
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.16);
  border-radius: var(--radius-pill);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition:
    color 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: var(--color-text-strong);
    transform: translateX(-2px);
    border-color: rgba(var(--color-brand-400-rgb), 0.28);
  }
`;

export const ShareButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  padding: 6px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
  background: rgba(31, 42, 64, 0.5);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.22);
    background: rgba(var(--color-brand-300-rgb), 0.08);
  }

  svg path {
    stroke: var(--color-text-strong);
  }
`;

/* ================ Hero ================ */

export const Hero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
  border-radius: 24px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
  background:
    radial-gradient(
      ellipse at 90% 0%,
      rgba(var(--color-brand-400-rgb), 0.1),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(22, 32, 52, 0.98) 0%,
      rgba(14, 20, 32, 0.99) 100%
    );
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const Eyebrow = styled.p`
  margin: 0;
  color: var(--color-brand-500);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.03em;
`;

export const IntroText = styled.p`
  margin: 0;
  max-width: 780px;
  color: var(--color-text-body);
  font-size: 15px;
  line-height: normal;
  opacity: 0.86;
`;

export const HeroSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 780px;
`;

/* ================ Main grid ================ */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 18px;
  width: 100%;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

/* ---- Preview (After) column ---- */

export const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 22px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.1);
  background:
    radial-gradient(
      ellipse at 0% 100%,
      rgba(var(--color-brand-400-rgb), 0.06),
      transparent 48%
    ),
    linear-gradient(
      180deg,
      rgba(22, 32, 52, 0.88) 0%,
      rgba(14, 20, 32, 0.94) 100%
    );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  position: sticky;
  top: 16px;

  @media (max-width: 980px) {
    position: static;
  }
`;

export const SectionLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PreviewMediaWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || 0.7293447293447294};
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.1);
  background: rgba(18, 24, 36, 0.5);
`;

export const MainImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const MainVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 65%,
    rgba(7, 12, 20, 0.45) 100%
  );
`;

export const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 2px 0;
`;

export const Tags = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  min-height: 24px;
  border-radius: 8px;
  border: 0.5px solid rgba(var(--color-brand-300-rgb), 0.1);
  background: rgba(var(--color-brand-400-rgb), 0.06);
  box-shadow: inset 0 1px 0.4px rgba(255, 255, 255, 0.05);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 0;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.08);
`;

export const InfoLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

export const InfoValue = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  text-align: left;
  flex: 1;
`;

/* ---- Compose (Before) column ---- */

export const ComposeCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: 22px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
  background:
    radial-gradient(
      ellipse at 100% 0%,
      rgba(var(--color-brand-400-rgb), 0.08),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(22, 32, 52, 0.94) 0%,
      rgba(14, 20, 32, 0.98) 100%
    );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

export const TabsContainer = styled.div`
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.1);
  background: rgba(14, 20, 32, 0.7);
  display: flex;
  padding: 3px;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $activeTab }) =>
      $activeTab === "IMAGE_TO_VIDEO" ? "3px" : "calc(50% + 1px)"};
    width: calc(50% - 4px);
    height: calc(100% - 6px);
    background: linear-gradient(
      180deg,
      rgba(var(--color-brand-400-rgb), 0.22) 0%,
      rgba(var(--color-brand-500-rgb), 0.18) 100%
    );
    border: 1px solid rgba(var(--color-brand-400-rgb), 0.3);
    box-shadow:
      0 0 0 1px rgba(var(--color-brand-400-rgb), 0.08),
      0 14px 30px rgba(var(--color-brand-500-rgb), 0.14),
      inset 0 1px 0 rgba(255, 255, 255, 0.07);
    border-radius: 11px;
    transition: left 240ms var(--ease-snappy);
    z-index: 1;
  }
`;

export const TabButton = styled.button`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 11px;
  cursor: pointer;
  position: relative;
  z-index: 2;
  padding: 0;
  transition: opacity 180ms var(--ease-snappy);

  svg path,
  svg rect,
  svg circle,
  svg line {
    stroke: ${({ $selected }) =>
      $selected ? "var(--color-text-strong)" : "var(--color-text-muted)"};
    transition: stroke 180ms var(--ease-snappy);
  }
`;

export const TabText = styled.span`
  color: ${({ $selected }) =>
    $selected ? "var(--color-text-strong)" : "var(--color-text-muted)"};
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.01em;
`;

export const UploadContainer = styled.div`
  height: 280px;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 18px;
  border: 1px dashed rgba(var(--color-brand-300-rgb), 0.2);
  background: rgba(18, 24, 36, 0.5);
  cursor: pointer;
  transition:
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  &:hover {
    border-color: rgba(var(--color-brand-400-rgb), 0.3);
    background: rgba(18, 24, 36, 0.7);
  }

  @media (max-width: 768px) {
    height: 240px;
  }
`;

export const UploadEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 20px;
`;

export const UploadIconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(
    180deg,
    rgba(var(--color-brand-400-rgb), 0.2) 0%,
    rgba(var(--color-brand-500-rgb), 0.12) 100%
  );
  border: 1px solid rgba(var(--color-brand-400-rgb), 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  svg path {
    stroke: var(--color-text-strong);
  }
`;

export const UploadHint = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

export const UploadSubHint = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

export const UploadedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

export const UploadedVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

export const UploadOverlayBtn = styled.button`
  border-radius: var(--radius-pill);
  background: linear-gradient(
    180deg,
    rgba(31, 42, 64, 0.85) 0%,
    rgba(18, 24, 36, 0.9) 100%
  );
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
  box-shadow:
    0 8px 18px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translate(-50%);
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  min-height: 36px;
  cursor: pointer;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translate(-50%, -1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.24);
  }
`;

export const PreviewDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  outline: none;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.12);
  position: absolute;
  top: 8px;
  left: 8px;
  border-radius: 10px;
  background: rgba(31, 42, 64, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
  justify-content: center;
  z-index: 2;
  width: 34px;
  height: 34px;
  padding-bottom: 2px;

  svg path {
    stroke: var(--color-text-strong);
  }

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.08);
  }
`;

export const PreviewCropBtn = styled.button`
  display: flex;
  align-items: center;
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px;
  outline: none;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.12);
  border-radius: 10px;
  background: rgba(31, 42, 64, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
  justify-content: center;
  z-index: 2;

  svg path {
    stroke: var(--color-text-strong);
  }

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.08);
  }
`;

export const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.08);
`;

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(18, 24, 36, 0.4);
  cursor: pointer;
  transition: background 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-400-rgb), 0.04);
  }
`;

export const OptionText = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

export const ToggleTrack = styled.div`
  width: 38px;
  height: 22px;
  border-radius: 11px;
  background: ${({ $active }) =>
    $active ? "var(--color-brand-500)" : "rgba(31, 42, 64, 0.7)"};
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(var(--color-brand-400-rgb), 0.3)"
        : "rgba(var(--color-brand-300-rgb), 0.12)"};
  position: relative;
  flex-shrink: 0;
  transition: all 200ms var(--ease-snappy);
`;

export const ToggleThumb = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-text-strong);
  position: absolute;
  top: 2px;
  left: ${({ $active }) => ($active ? "18px" : "2px")};
  transition: left 200ms var(--ease-snappy);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;

/* ---- Generate row ---- */

export const GenerateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 5;
`;

export const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.1);
  background: rgba(31, 42, 64, 0.4);
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.22);
  }
`;

export const InfoIconWrapper = styled(StyledIconWrapper)`
  svg path {
    stroke: ${(p) => (p.$active ? "var(--color-brand-500)" : "#fff")};
  }
`;

export const TooltipContainer = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
  background:
    radial-gradient(
      circle at top right,
      rgba(var(--color-brand-400-rgb), 0.1),
      transparent 42%
    ),
    linear-gradient(
      180deg,
      rgba(24, 34, 54, 0.98) 0%,
      rgba(14, 20, 32, 0.99) 100%
    );
  padding: 14px;
  position: absolute;
  bottom: 52px;
  right: 0;
  width: 320px;
  gap: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 16px 32px rgba(0, 0, 0, 0.3),
    0 4px 8px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 280px;
  }
`;

export const TooltipText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  opacity: ${(p) => (p.$light ? 0.6 : 1)};
`;

/* ================ How to use ================ */

export const HowToSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  border-radius: 22px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.12);
  background: linear-gradient(
    180deg,
    rgba(22, 32, 52, 0.7) 0%,
    rgba(14, 20, 32, 0.8) 100%
  );
`;

export const HowToTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const HowToGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const HowToStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.08);
  background: rgba(18, 24, 36, 0.5);
`;

export const HowToStepNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(
    180deg,
    rgba(var(--color-brand-400-rgb), 0.22) 0%,
    rgba(var(--color-brand-500-rgb), 0.18) 100%
  );
  border: 1px solid rgba(var(--color-brand-400-rgb), 0.3);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
`;

export const HowToStepText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  opacity: 0.88;
`;

/* ================ Related / Suggested ================ */

export const RelatedSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const RelatedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const RelatedTitle = styled.h2`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const BrowseMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-brand-500);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 180ms var(--ease-snappy);

  &:hover {
    opacity: 0.85;
  }
`;

export const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const RelatedCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.12);
  background:
    radial-gradient(
      ellipse at 100% 0%,
      rgba(var(--color-brand-400-rgb), 0.1),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(22, 32, 52, 0.85) 0%,
      rgba(14, 20, 32, 0.92) 100%
    );
  color: inherit;
  text-decoration: none;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(var(--color-brand-400-rgb), 0.28);
  }
`;

export const RelatedCardEyebrow = styled.span`
  color: var(--color-brand-500);
  font-family: var(--font-family-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const RelatedCardTitle = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: normal;
`;

export const RelatedCardArrow = styled.span`
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-muted);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

/* ================ Skeletons ================ */

export const SkeletonBox = styled.div`
  background: linear-gradient(
    180deg,
    rgba(28, 43, 68, 0.9) 0%,
    rgba(18, 26, 40, 0.96) 100%
  );
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.12);
  border-radius: ${(p) => p.$radius || "12px"};
  width: ${(p) => p.$width || "100%"};
  ${(p) => p.$maxWidth && `max-width: ${p.$maxWidth};`}
  height: ${(p) => (p.$aspectRatio ? "auto" : p.$height || "20px")};
  ${(p) => p.$aspectRatio && `aspect-ratio: ${p.$aspectRatio};`}
  position: relative;
  overflow: hidden;
  isolation: isolate;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(
      115deg,
      rgba(var(--color-brand-300-rgb), 0) 0%,
      rgba(var(--color-brand-300-rgb), 0) 30%,
      rgba(var(--color-brand-300-rgb), 0.08) 42%,
      rgba(var(--color-brand-300-rgb), 0.18) 50%,
      rgba(var(--color-brand-300-rgb), 0.08) 58%,
      rgba(var(--color-brand-300-rgb), 0) 70%,
      rgba(var(--color-brand-300-rgb), 0) 100%
    );
    pointer-events: none;
    will-change: transform;
    animation: ${shimmerSweep} 1.6s ease-in-out infinite;
  }
`;

export const ToastWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const CopyContainer = styled.div`
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.18);
  background: rgba(var(--glass-white-rgb), 0.04);
  cursor: pointer;
  transition: background 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--glass-white-rgb), 0.08);
  }
`;
